import { useState, useEffect, useRef } from 'react'

import { RichTextProps } from '../../core/RichText/RichText'
import { ImageProps } from '../../core/Image/Image'
import { LinkProps } from '../../core/Link/Link'
import HomeCard from './HomeCard'
import DeviceCard from './DeviceCard'
import { IconProps } from '../../core/Icon/Icon'
import { Card, FlexGrid } from '@telus-uds/components-web'
import { VIEWPORT } from '@/constants/VIEWPORT'
import { HeadingProps } from '../../core/Heading/Heading'
import { ButtonLinkProps } from '../../core/ButtonLink/ButtonLink'

export type CalcValue = {
  icon?: IconProps
  name: string
  savingsValues: { key: string; value: number }
  tooltip?: string
}

export type SavingsCalculatorProps = {
  homeHeader: RichTextProps
  homeImage: ImageProps
  deviceHeader: { fields: RichTextProps }
  savingsHeader: { fields: HeadingProps }
  savingsRateText: string
  provinceDropdown: {
    label: string
    options: { value: string; label: string }[]
    hint: string
    customErrorMessage: string
  }
  homeLabel: string
  homeError: string
  energyLabel: string
  energyError: string
  buttonText: string
  restartButtonText: string
  homeValueLabel: string
  homeValues: {
    [province: string]: {
      [energyType: string]: {
        [residenceType: string]: number
      }
    }
  }
  deviceValueLabel: string
  deviceValues: CalcValue[]
  rewardsValueLabel: string
  rewardsValues: CalcValue
  rewardLink: LinkProps
  step2CTA: string
  step3CTAProps: ButtonLinkProps
  step3SubHeading: string
}

const SavingsCalculator = ({
  homeHeader,
  homeImage,
  deviceHeader,
  savingsHeader,
  savingsRateText,
  provinceDropdown,
  homeLabel,
  homeError,
  energyLabel,
  energyError,
  buttonText,
  restartButtonText,
  homeValueLabel,
  homeValues,
  deviceValueLabel,
  deviceValues,
  rewardsValueLabel,
  rewardsValues,
  rewardLink,
  step2CTA,
  step3CTAProps,
  step3SubHeading,
}: SavingsCalculatorProps) => {
  const [cardView, setCardView] = useState('home')
  const [province, setProvince] = useState(provinceDropdown.options[0].value)
  const [homeSavings, setHomeSavings] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [hasGutter, setHasGutter] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  const homeProps = {
    homeHeader,
    homeImage,
    provinceDropdown,
    homeLabel,
    homeError,
    energyLabel,
    energyError,
    buttonText,
    homeValues,
    setCardView,
    setHomeSavings,
    setProvince,
    province,
    isMobile,
  }

  const deviceProps = {
    deviceHeader,
    savingsHeader,
    savingsRateText,
    restartButtonText,
    homeValueLabel,
    deviceValueLabel,
    deviceValues,
    rewardsValueLabel,
    rewardsValues,
    rewardLink,
    setCardView,
    homeSavings,
    province,
    isMobile,
    homeImage,
    isTablet,
    step2CTA,
    step3CTAProps,
    step3SubHeading,
  }

  const prevWidth = useRef(0)

  const windowResize = () => {
    setIsMobile(window.innerWidth < VIEWPORT.md)
    setIsTablet(window.innerWidth < VIEWPORT.lg)
    setHasGutter(window.innerWidth < VIEWPORT.lg)
    if (window.innerWidth !== prevWidth.current) {
      prevWidth.current = window.innerWidth
    }
  }

  useEffect(() => {
    prevWidth.current = window.innerWidth
    windowResize()
    window.addEventListener('resize', windowResize)

    return () => window.removeEventListener('resize', windowResize)
  }, [])

  return (
    <FlexGrid gutter={hasGutter}>
      <Card variant={{ padding: 'custom', borderRadius: 'large' }}>
        {cardView === 'home' ? <HomeCard {...homeProps} /> : <DeviceCard {...deviceProps} />}
      </Card>
    </FlexGrid>
  )
}

export default SavingsCalculator
